import { FilterService } from '../../../services/filter.service';
import { CompoundFilterExpression, ExportDataOptions, ExportFilter, FilterExpression } from '../../../services/data.service';
import { ExportOptions, ExportService, ExportType, Orientation } from '../../../services/export.service';
import { take } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { dutchDate, getTimestamp } from '@cumlaude/shared-utils';
import { combineLatestWith, from, lastValueFrom, Observable, of, Subscription, throwError } from 'rxjs';
import { Exportable, TableExport, TableExportData } from '../../../services/exportable';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { computed, Directive, inject, OnDestroy, signal } from '@angular/core';
import { sortBy } from 'lodash-es';
import html2canvas from 'html2canvas-pro';
import { LoadingService, LoadingType } from '../../../services/loading.service';
import { UrlService } from '../../../services/url.service';
import { getFilters } from '../../../services/metadata';
import { AttOverrides } from '@cumlaude/shared-dashboard-registry';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { attOverridesUitRouteData } from '../../../services/user-routes.service';
import { Att, Table } from '@cumlaude/metadata';
import { AutorisatieService } from '../../../services/autorisatie.service';
import { Attributes } from './base-dashboard-config';
import { UserService } from '../../../services/user.service';
import { BugsnagService } from '@cumlaude/bugsnag';
import { ICON_REGISTRY } from '@cumlaude/shared-components-icons';
import { GtmService } from '../../../services/gtm.service';

@Directive()
export abstract class Dashboard implements Exportable, OnDestroy {
	protected readonly filterService = inject(FilterService);
	protected readonly toastr = inject(ToastrService);
	protected readonly exportService = inject(ExportService);
	protected readonly loadingService = inject(LoadingService);
	protected readonly urlService = inject(UrlService);
	protected readonly autorisatieService = inject(AutorisatieService);
	private readonly bugsnagService = inject(BugsnagService);
	protected readonly userService = inject(UserService);
	private readonly gtm = inject(GtmService);

	protected readonly routeData = toSignal(inject(ActivatedRoute).data);

	abstract factTable: Table;

	readonly filterExpressions = signal<FilterExpression[]>([]);

	protected subscriptions: Subscription[] = [];

	doExport(filters: FilterExpression[], permanentFilters: FilterExpression[], exportOptions: ExportOptions) {
		const allFilters = [...permanentFilters, ...filters];
		const filterLabelAndValue = filters.length > 0 ? this.filterService.getFilterLabelAndValue() : of([]);
		lastValueFrom(filterLabelAndValue.pipe(combineLatestWith(this.urlService.routeData$), take(1))).then(([values, routeData]) =>
			this.processExport(exportOptions, allFilters, values, routeData.path)
		);
	}

	private processExport(exportOptions: ExportOptions, allFilters: FilterExpression[], filters: ExportFilter[], fileName: string) {
		let exportFilters = this.getExportFilters(filters);
		const exportDataOptions = {
			showDatum: exportOptions.showDatum,
			orientation: exportOptions.orientation,
			f: allFilters.length == 1 ? allFilters[0] : new CompoundFilterExpression(allFilters),
			attrs: [],
			attrLabels: [],
			...(exportOptions.showTitle ? { title: fileName } : {}),
			...(exportOptions.showFilters ? { filters: [...exportFilters, ...this.getDisplayOptions()] } : {}),
			...(exportOptions.showLegenda ? { legendaEntries: exportOptions.legendaEntries, legendaStyle: exportOptions.legendaStyle } : {}),
		};

		const exportType = exportOptions.type;
		this.gtm.pushEvent('exporteren', { type: exportType });

		switch (exportType) {
			case ExportType.AFBEELDING:
				this.downloadExport(this.exportToPng(exportDataOptions), fileName, 'png');
				break;
			case ExportType.PDF:
				this.downloadExport(this.exportToPdf(exportDataOptions), fileName, 'pdf');
				break;
			case ExportType.TABEL:
				this.downloadExport(this.exportAsTable(exportOptions, fileName, exportFilters), fileName, 'xlsx');
				break;
			case ExportType.DATA:
				for (const dataExport of exportOptions.dataExports) {
					const sortedSelectedColumns = sortBy(dataExport.attrs, (att) => att.label);
					const dataExportOptions = {
						...exportDataOptions,
						attrs: sortedSelectedColumns.map((att) => <Att>att.att),
						attrLabels: sortedSelectedColumns.map((att) => att.label!),
					};
					this.downloadExport(this.getExportData(dataExportOptions), dataExport.naam ?? fileName, 'xlsx');
				}
				break;
		}
	}

	private downloadExport(blob: Observable<Blob>, fileName: string, extension: string) {
		blob.subscribe({
			next: (blob) => {
				try {
					saveAs(blob, `CumLaude ${fileName} ${getTimestamp()}.${extension}`);
					this.loadingService.stop(LoadingType.EXPORT);
					this.toastr.success(`Export ${fileName} succesvol.`);
				} catch (e) {
					this.sendToastError();
				}
			},
			error: (error) => {
				this.bugsnagService.notify(error);
				this.sendToastError();
			},
		});
	}

	allFilters = computed(() => getFilters(this.factTable, this.attOverrides));

	/**
	 * De attribuut-overrides die in de dashboard-registry bij dit dashboard staan. Ze bepalen wat de
	 * gebruiker kan toevoegen aan Alle filters, Alle groeperingen en de export; wat het dashboard zelf
	 * gebruikt staat los daarvan in het component.
	 *
	 * Komt uit de route-data en niet uit de huidige URL, zodat de waarde er al is op het moment dat het
	 * component wordt opgebouwd — `ngAfterViewInit` leest de groeperingen eenmalig.
	 */
	get attOverrides(): AttOverrides {
		return attOverridesUitRouteData(this.routeData());
	}

	private sendToastError() {
		this.toastr.error('Er ging iets mis bij het exporteren. Probeer opnieuw.');
	}

	getExportData(_options: ExportDataOptions): Observable<Blob> {
		return throwError(() => new Error('Export not implemented for this dashboard.'));
	}

	exportAsTable(_exportOptions: ExportOptions, _title: string, _filters: ExportFilter[]): Observable<Blob> {
		return throwError(() => new Error('Export not implemented for this dashboard.'));
	}

	buildSinglePageExportTable<A extends Attributes>(
		exportOptions: ExportOptions,
		title: string,
		filters: ExportFilter[],
		data: TableExportData<A>[]
	): TableExport<A> {
		return {
			showDatum: exportOptions.showDatum,
			pages: [
				{
					name: 'CumLaude data',
					...(exportOptions.showTitle ? { title } : {}),
					...(exportOptions.showFilters ? { filters, displayOptions: this.getDisplayOptions() } : {}),
					data,
				},
			],
		};
	}

	exportToPdf(options: ExportDataOptions): Observable<Blob> {
		const html = ['<html class="princexml"><head>'];
		html.push(`<base href="${location.protocol}//${location.host}/"/>`);
		document.querySelectorAll('head link[rel=stylesheet],title,style').forEach((el) => html.push(el.outerHTML));

		const datum = options.showDatum ? `@bottom-left{content:'${dutchDate(new Date())}';}}` : '';

		html.push(
			`<style type="text/css">@page{size:A4 ${options.orientation === Orientation.LANDSCAPE ? 'landscape' : ''};${datum}</style>`,
			'</head>',
			`<body class="${getDashboardClasses().join(' ')}">`,
			this.createExportHeader(options.title)
		);
		document.querySelectorAll('[data-exportable]').forEach((el) => html.push(el.outerHTML));
		html.push(this.createExportFiltersAndDatum(false, options.filters), this.createExportLegenda(options), '</body></html>');
		const doc = html.join(' ');
		return this.exportService.exportToPdf(doc);
	}

	/**
	 * Override deze methode om de "filters" bovenaan de export aan te passen.
	 */
	protected getExportFilters(exportFilters: ExportFilter[]): ExportFilter[] {
		return exportFilters;
	}

	/**
	 * Override deze methode om extra display opties toe te voegen aan de export op basis van de naam van het dashboard component.
	 */
	public getDisplayOptions(): ExportFilter[] {
		return [];
	}

	/**
	 * Bepaal de werkelijk gebruikte breedte en hoogte van een app-card-list
	 * op basis van de gerenderde kaarten, niet de host-breedte.
	 */
	protected getCardListSize(element: HTMLElement): { width: number; height: number } {
		const cards = Array.from(element.querySelectorAll(':scope > .container > .element')).filter(
			(card): card is HTMLElement => card instanceof HTMLElement
		);
		if (cards.length === 0) {
			const { width, height } = element.getBoundingClientRect();
			return { width: Math.ceil(width), height: Math.ceil(height) };
		}

		const cardRects = cards.map((card) => card.getBoundingClientRect());
		const usedWidth = Math.max(0, Math.max(...cardRects.map((rect) => rect.right)) - Math.min(...cardRects.map((rect) => rect.left)));
		const usedHeight = Math.max(0, Math.max(...cardRects.map((rect) => rect.bottom)) - Math.min(...cardRects.map((rect) => rect.top)));
		return { width: Math.ceil(usedWidth), height: Math.ceil(usedHeight) };
	}

	exportToPng(options: ExportDataOptions): Observable<Blob> {
		this.loadingService.start(LoadingType.EXPORT, true);

		let headerLayer: HTMLElement | null = null;
		if (options.title || options.filters || options.showDatum || options.legendaEntries) {
			headerLayer = document.createElement('div');
			headerLayer.classList.add('html2canvas-header', ...getDashboardClasses());
			headerLayer.dataset['exportable'] = '';
			headerLayer.innerHTML =
				this.createExportHeader(options.title) +
				this.createExportFiltersAndDatum(options.showDatum, options.filters) +
				this.createExportLegenda(options);
			document.body.insertBefore(headerLayer, document.body.firstChild);
		}

		const elements = document.querySelectorAll('[data-exportable]');

		// Bepaal waar alle elementen moeten komen en hoe groot het canvas moet worden
		const GAP = 16;
		const MARGIN = 16;
		const BROWSER_WIDTH = Math.max(document.documentElement.clientWidth, window.innerWidth || 0) + 2 * MARGIN;
		let totalHeight = MARGIN;
		let maxWidth = 0;

		const positionedElements: { element: HTMLElement; left: number; top: number; width: number; height: number }[] = [];
		elements.forEach((element) => {
			if (!(element instanceof HTMLElement)) return;

			// Scroll helemaal naar linksboven om te voorkomen dat de header op de verkeerde plaats komt.
			element.closest('.cdk-virtual-scrollable')?.scrollTo(0, 0);
			element.classList.add('html2canvas');

			if (totalHeight > MARGIN) totalHeight += GAP;

			let height: number;
			let width: number;
			if (element.matches('app-card-list')) {
				const { width: cardListWidth, height: cardListHeight } = this.getCardListSize(element);
				width = Math.max(0, Math.min(cardListWidth + 2 * MARGIN, BROWSER_WIDTH) - 2 * MARGIN);
				height = cardListHeight;
			} else {
				const { width: hostWidth, height: hostHeight } = element.getBoundingClientRect();
				width = Math.ceil(hostWidth);
				height = Math.ceil(hostHeight);
			}

			positionedElements.push({ element, left: MARGIN, top: totalHeight, width, height });
			totalHeight += height;
			maxWidth = Math.max(maxWidth, width + 2 * MARGIN);
		});

		totalHeight += MARGIN;

		const canvas = document.createElement('canvas');
		canvas.width = maxWidth;
		canvas.height = totalHeight;

		const ctx = canvas.getContext('2d');
		if (!ctx) throw new Error('Cannot create canvas context');

		// Begin met een witte achtegrond
		ctx.fillStyle = 'white';
		ctx.fillRect(0, 0, maxWidth, totalHeight);

		// Teken alle exporteerbare elementen op de juiste plaats op het canvas
		const promises = positionedElements.map(
			({ element, left, top, width, height }) =>
				new Promise<void>((resolve, reject) =>
					setTimeout(
						() =>
							html2canvas(element, { scale: 1, logging: false })
								.then((c) => {
									ctx.drawImage(c, 0, 0, width, height, left, top, width, height);
									resolve();
								})
								.catch((err) => reject(err)),
						0
					)
				)
		);
		// Als alles klaar is, maak er dan een Blob van met de PNG-data
		const blob = Promise.all(promises).then(() => {
			headerLayer?.remove();
			elements.forEach((element) => element.classList.remove('html2canvas'));
			return new Promise<Blob>((resolve, reject) =>
				canvas.toBlob((b) => (b ? resolve(b) : reject('Canvas could not be exported')), 'image/png')
			);
		});
		return from(blob);
	}

	protected createExportHeader(title?: string): string {
		if (title) return `<h2 class="header">${title}</h2>`;
		return '';
	}

	protected createExportLegenda(options: ExportDataOptions): string {
		const html: string[] = [];

		const { legendaEntries, legendaStyle } = options;
		if (legendaEntries) {
			html.push('<div class="legenda-options">');
			legendaEntries.forEach((entry) => {
				html.push(`<div class="option"><div class="legenda ${entry.class}${entry.icon ? ' icon' : ''}">`);
				if (entry.icon) {
					const svg = ICON_REGISTRY[entry.icon];
					if (svg) {
						html.push(entry.rotate ? svg.replace('<svg ', `<svg style="transform: rotate(${entry.rotate}deg)" `) : svg);
					}
				}
				if (legendaStyle === 'PILL' && entry.value === 'Geslaagd') {
					html.push('<div class="vink">\u1E12</div>');
				}
				html.push(`</div><span class="label">${entry.value}</span></div>`);
			});
			html.push('</div>');
		}

		return html.join('');
	}

	protected createExportFiltersAndDatum(showDatum: boolean, filters?: ExportFilter[]): string {
		const html: string[] = [];
		if (filters || showDatum) {
			html.push('<dl class="filters">');

			if (filters) filters.forEach((f) => html.push(`<dt>${f.label}</dt><dd>${f.value}</dd>`));
			if (showDatum) html.push(`<dt>Datum</dt><dd>${dutchDate(new Date())}</dd>`);

			html.push('</dl>');
		}
		return html.join('');
	}

	ngOnDestroy() {
		for (const sub of this.subscriptions) sub.unsubscribe();
	}
}

/** De "dashboard-xxx"-classes regelen de juiste kleuren in het dashboard en de legenda */
export function getDashboardClasses(): string[] {
	return Array.from(document.querySelector('[class^=dashboard-]')?.classList ?? []).filter((cl) => cl.startsWith('dashboard-')) ?? [];
}
